import { supportedLocales } from "@cronika/locales";
import { Modal, ModalBody, ModalContent, ModalHeader, ModalTitle } from "@cronika/ui";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { localeStorage } from "@/shared/lib/locale-storage";
type TLanguageModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const languageLabels: Record<string, { label: string; code: string }> = {
  en: { label: "English", code: "EN" },
  ru: { label: "Русский", code: "RU" },
  kk: { label: "Қазақша", code: "KZ" },
  bn: { label: "বাংলা", code: "BN" },
  id: { label: "Bahasa Indonesia", code: "ID" },
  pt: { label: "Português", code: "PT" },
  es: { label: "Español", code: "ES" },
  ua: { label: "Українська", code: "UA" },
};

const languages = supportedLocales.map((key) => ({
  key,
  label: languageLabels[key]?.label || key,
  code: languageLabels[key]?.code || key.toUpperCase(),
}));

export const LanguageModal: FC<TLanguageModalProps> = ({ open, setOpen }) => {
  const { t, i18n } = useTranslation("layout");
  const navigate = useNavigate();
  const location = useLocation();

  const currentLanguageKey = i18n.language || "en";

  const changeLang = (newLang: string) => {
    const mappedLang = supportedLocales.includes(newLang as any) ? newLang : "en";

    // Remove current locale from pathname if present
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0] ?? "";
    const isLocale = supportedLocales.includes(firstSegment as any);

    const cleanPath = isLocale ? "/" + pathSegments.slice(1).join("/") : location.pathname;

    const newPath = `/${mappedLang}${cleanPath === "/" ? "" : cleanPath}`;

    navigate(newPath);
    i18n.changeLanguage(mappedLang);
    localeStorage.set(mappedLang);
    setOpen(false);
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size="sm" className="bg-zinc-800 border-zinc-700">
        <ModalHeader className="border-b border-zinc-700 px-6 py-4">
          <ModalTitle className="text-white text-lg font-medium">{t("language-modal.title")}</ModalTitle>
        </ModalHeader>
        <ModalBody className="p-0">
          <div className="flex flex-col">
            {languages.map((language) => (
              <div
                key={language.key}
                className={`flex items-center gap-3 px-6 py-4 cursor-pointer transition-colors ${
                  currentLanguageKey === language.key ? "bg-zinc-700" : "hover:bg-zinc-700/50"
                }`}
                onClick={() => changeLang(language.key)}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="bg-white text-zinc-600 px-2 py-1 rounded text-xs font-medium">{language.code}</div>
                  <span className="text-white text-sm">{language.label}</span>
                </div>
              </div>
            ))}
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
